import { ACTIVE_VARIANTS } from './enums.js';

/**
 * Sagoma del pezzo. Forme simmetriche a 180° (tondo, ciambella, quadrato, rombo)
 * e forme fantasia asimmetriche, che vanno contro-ruotate a scacchiera girata.
 */
export type PieceShape =
  | 'round'
  | 'donut'
  | 'square'
  | 'rhombus'
  | 'triangle'
  | 'star'
  | 'heart';

/**
 * GEOMETRIA del pezzo — e nient'altro. Il materiale (legno, marmo, vetro,
 * plastica) è un asse separato: qui si decide solo se il pezzo ha spessore, se
 * la faccia è incisa, e come la dama guadagna volume rispetto alla pedina.
 */
export type PieceStyle =
  // Corpo estruso; la dama è due dischi impilati con una giuntura a metà.
  | '3d-stacked-engraved'
  | '3d-stacked-smooth'
  // Corpo estruso; la dama porta una corona 3D sopra la faccia, come se il pezzo
  // sotto fosse la testa del re.
  | '3d-crown'
  // Nessuno spessore: la dama si distingue solo per l'icona, che è obbligatoria.
  | 'flat-plain'
  | 'flat-engraved';

/**
 * Emblema che marca la dama, disegnato sulla faccia più alta del pezzo.
 *
 * 'none' è ammessa SOLO sugli stili 3D, dove il volume (doppio disco o corona)
 * distingue già la dama dalla pedina. Su uno stile piatto renderebbe i due pezzi
 * identici, quindi è filtrata dalle impostazioni e riportata a 'star' a runtime.
 */
export type KingIcon = 'none' | 'star' | 'crown' | 'ring' | 'diamond';

/**
 * TIPO DI CORONA dello stile "3D con corona": la forma del volume che la dama porta in
 * testa, non un emblema disegnato sulla faccia.
 *
 * È un asse SUO e non un riuso di `KingIcon`, che è la strada percorsa all'inizio: con una
 * preferenza sola, scegliere "imperiale" sulla corona cambiava l'emblema degli stili piatti
 * in "anello" e viceversa — due scelte che si sovrascrivevano a vicenda. Valori propri anche
 * per leggibilità: `crownType: 'imperial'` si capisce, `kingIcon: 'ring'` per dire imperiale
 * era un enigma.
 */
export type CrownType = 'classic' | 'plain' | 'diadem' | 'lily' | 'imperial';

/**
 * Notazione disegnata SULLE caselle della scacchiera: nessuna, il numero di casella
 * (quello del FEN e della lista mosse) o la coordinata algebrica stile scacchi.
 *
 * È una preferenza d'ASPETTO come le altre di questo file — vale su ogni scacchiera che
 * la disegna (partita, puzzle, analisi, revisione, editor) — e parte da 'none': una
 * scacchiera piena di etichette non è ciò che ci si aspetta di trovare la prima volta.
 */
export type BoardNotation = 'none' | 'numeric' | 'algebraic';

/**
 * Materiale simulato, usato sia per le pedine sia per la scacchiera. Determina la
 * luce (distribuzione del gradiente, riflessi) e soprattutto le VENATURE: assenti
 * su plastica (opaca) e vetro (che però è lucido), appena accennate sul marmo,
 * marcate sul legno.
 */
export type Material = 'plastic' | 'marble' | 'glass' | 'wood';

/**
 * Coppia di colori delle pedine. Convenzione dell'id: `<lato chiaro>-<lato scuro>`,
 * nello stesso ordine dei campi `white`/`dark` del ColorPair che lo implementa.
 *
 * ECCEZIONE: i quattro id che portano il nome di una VARIANTE. Non descrivono due tinte,
 * riproducono i colori con cui quella variante si gioca — gli stessi che 'auto' applica da
 * sé mentre la si sta giocando, qui scegliibili anche giocando a un'altra. I loro
 * esadecimali non stanno da nessuna parte: si derivano dalla MasterMatrix (vedi
 * `piece-themes.ts`), o al primo ritocco di `variants.jsonc` l'"Italiana" smetterebbe in
 * silenzio di essere l'italiana.
 */
export type PieceColorTheme =
  | 'auto'
  /** Coppia scelta a mano dall'utente: i due esadecimali stanno in `customPiece*`. */
  | 'custom'
  | 'international'
  | 'italian'
  | 'english'
  | 'brazilian'
  | 'classic'
  | 'red-black'
  | 'white-black'
  | 'blue-red'
  | 'orange-blue'
  | 'silver-gold'
  | 'coral-teal'
  | 'green-purple'
  | 'pink-navy';

export type BoardTheme =
  | 'auto'
  /** Coppia scelta a mano dall'utente: i due esadecimali stanno in `customBoard*`. */
  | 'custom'
  /**
   * La scacchiera dell'INTERNAZIONALE, sotto il nome del legno: `#F0D9B5/#B58863` sono
   * esattamente i due colori che la MasterMatrix dà a quella variante. Per questo non
   * esiste un id 'international' qui accanto — sarebbe la stessa scacchiera due volte —
   * ed è questo id a portarne l'etichetta: "Internazionale", senza altre qualifiche.
   * Il nome del legno resta un fatto interno: chi sceglie non deve sapere che l'id si
   * chiama 'wood-classic', e la stessa voce fra le pedine si legge già così.
   */
  | 'wood-classic'
  | 'italian'
  | 'english'
  | 'brazilian'
  | 'walnut'
  | 'marble'
  | 'green-cream'
  | 'blue-grey'
  | 'high-contrast'
  | 'mahogany'
  | 'ocean'
  | 'purple-lavender'
  | 'rose';

/** Preferred time-control category (mirrors the dashboard cadence selector and the 8-rating system). */
export type PreferredCadence = 'bullet' | 'blitz' | 'rapid' | 'classical';

/**
 * Il tema della CORNICE dell'app: fondo, card, vetro, inchiostro, modali.
 *
 * NON e' il tema della scacchiera, che e' un asse suo (`boardTheme`) e resta
 * quello scelto nelle preferenze anche al buio: le caselle sono legno, e i
 * suggerimenti di mossa sono tarati su quelle. Qui si decide soltanto cosa ci
 * sta attorno.
 *
 * Due valori e nessun 'system': il chiaro e' il default e il buio si chiede
 * esplicitamente, quindi `prefers-color-scheme` non viene letto da nessuna parte.
 * Allargarlo dopo e' sicuro sul server, ma non sul client: `clampEnum` riporta
 * al default ogni valore che non conosce, quindi una scheda col bundle
 * precedente rispedirebbe l'utente al chiaro in silenzio.
 */
export type UiTheme = 'light' | 'dark';

export interface UserPreferences {
  pieceShape: PieceShape;
  pieceStyle: PieceStyle;
  /** Emblema della dama. 'none' solo sugli stili 3D (vedi KingIcon). */
  kingIcon: KingIcon;
  /** Forma della corona sullo stile "3D con corona". Indipendente da `kingIcon`. */
  crownType: CrownType;
  pieceColorTheme: PieceColorTheme;
  /** Lato chiaro personalizzato delle pedine (esadecimale). Usato solo con `pieceColorTheme: 'custom'`. */
  customPieceLight: string;
  /** Lato scuro personalizzato delle pedine (esadecimale). */
  customPieceDark: string;
  /** Materiale delle pedine: luce e venature. */
  pieceMaterial: Material;
  boardTheme: BoardTheme;
  /** Casella chiara personalizzata (esadecimale). Usata solo con `boardTheme: 'custom'`. */
  customBoardLight: string;
  /** Casella scura personalizzata (esadecimale). */
  customBoardDark: string;
  /** Materiale della scacchiera: venatura continua su tutta la lastra. */
  boardMaterial: Material;
  /** Etichette sulle caselle: nessuna, numeri di casella, coordinate algebriche. */
  boardNotation: BoardNotation;
  /**
   * I tre assi audio sono INDIPENDENTI, non un interruttore generale con dei figli:
   * chi vuole sentire le proprie mosse in silenzio assoluto sul resto (o l'esatto
   * contrario) non deve passare da un master. Prima era un unico `soundEnabled`, che
   * governava anche un canale che l'etichetta non nominava nemmeno — le notifiche.
   *
   * Ogni suono di `audio.ts` dichiara la propria categoria e legge la chiave qui sotto;
   * un suono nuovo senza categoria non compila.
   */
  /** Mossa, trascinamento, promozione. */
  moveSoundEnabled: boolean;
  /** Allarme quando il PROPRIO orologio scende sotto soglia. */
  lowTimeSoundEnabled: boolean;
  /** Partita trovata, sfida in arrivo, richiesta di rivincita. */
  notificationSoundEnabled: boolean;
  vibrationEnabled: boolean;
  /** Show the green move-hint dots and capture-target rings while selecting a piece. */
  showMoveHints: boolean;
  showLastMove: boolean;
  showLastMoveCaptures: boolean;
  /** Lowercase variant string (e.g. 'international') — used for puzzles and other features. */
  preferredVariant: string;
  /** Preferred cadence category — seeds the dashboard default time control on first load. */
  preferredCadence: PreferredCadence;
  /** Daily-goal target: how many games/day the user aims to play (dashboard goal card). */
  dailyGoalTarget: number;
  /**
   * Allow queuing a pre-move during the opponent's turn (online / arena / tournament / vs-AI).
   *
   * OPT-IN, e non per prudenza generica: chi non sa cos'è una premossa tocca un proprio
   * pezzo mentre l'altro pensa e al cambio di turno si vede giocare una mossa che non ha
   * confermato. È una funzione che si sceglie, non che si subisce.
   */
  premoveEnabled: boolean;
  /** Tema della cornice dell'app — chiaro o scuro. La scacchiera non lo segue. */
  uiTheme: UiTheme;
}

export const DEFAULT_PREFERENCES: UserPreferences = {
  pieceShape: 'round',
  pieceStyle: '3d-stacked-engraved',
  // 'none' come primo valore di KING_ICONS: sullo stile di default la dama è il doppio
  // disco tornito, che è già il pezzo vero e si distingue dalla pedina per volume e
  // giuntura. Legale solo perché lo stile di default è 3D — vedi `isFlatStyle`.
  kingIcon: 'none',
  // La corona con le perle: è quella che si riconosce a colpo d'occhio, ed è la prima voce
  // dell'elenco — qui come nel menu.
  crownType: 'classic',
  pieceColorTheme: 'auto',
  // Punto di partenza del selettore: i toni classici legno/crema, così aprendolo si
  // trova qualcosa di sensato da spostare invece di due colori arbitrari.
  customPieceLight: '#E8D9B5',
  customPieceDark: '#5D4037',
  pieceMaterial: 'plastic',
  boardTheme: 'auto',
  customBoardLight: '#F0D9B5',
  customBoardDark: '#B58863',
  boardMaterial: 'plastic',
  boardNotation: 'none',
  moveSoundEnabled: true,
  lowTimeSoundEnabled: true,
  notificationSoundEnabled: true,
  vibrationEnabled: true,
  showMoveHints: true,
  showLastMove: true,
  showLastMoveCaptures: true,
  preferredVariant: 'international',
  preferredCadence: 'blitz',
  dailyGoalTarget: 1,
  premoveEnabled: false,
  uiTheme: 'light',
};

// L'ordine di questi array è l'ordine di visualizzazione nelle <select> delle
// impostazioni: il primo valore di ciascuno è anche il default.
export const PIECE_SHAPES: readonly PieceShape[] = [
  'round',
  'donut',
  'square',
  'rhombus',
  'heart',
  'star',
  'triangle',
];
export const PIECE_STYLES: readonly PieceStyle[] = [
  '3d-stacked-engraved',
  '3d-stacked-smooth',
  '3d-crown',
  'flat-plain',
  'flat-engraved',
];
export const KING_ICONS: readonly KingIcon[] = ['none', 'star', 'crown', 'ring', 'diamond'];
/**
 * I cinque tipi di corona. Come per gli altri elenchi del file, la PRIMA voce è il default e
 * apre il menu: la Classica, che è la corona più riconoscibile.
 */
export const CROWN_TYPES: readonly CrownType[] = ['classic', 'plain', 'diadem', 'lily', 'imperial'];
// L'ordine è anche il ciclo del pulsante "Notazione" nell'editor, e 'none' aprendo la
// lista è il default: niente etichette finché non se ne chiedono.
export const BOARD_NOTATIONS: readonly BoardNotation[] = ['none', 'numeric', 'algebraic'];
// La plastica apre l'elenco, quindi è il materiale di partenza: è l'unico che non
// impone nulla — nessuna venatura sulla lastra, superficie opaca sui pezzi — ed è la
// scacchiera che si vedeva prima che i materiali esistessero. Legno e marmo sono un
// carattere forte, e un carattere si sceglie: non lo si eredita senza averlo chiesto.
export const MATERIALS: readonly Material[] = ['plastic', 'marble', 'glass', 'wood'];
// I due elenchi di colori APRONO con le due voci che non sono una palette: 'auto', che
// è il default e risolve i colori sulla variante in corso, e subito dopo 'custom', che
// apre il selettore invece di applicare una tinta già pronta. Stanno insieme in cima
// perché sono le due risposte a "non voglio scegliere fra queste": la prima lascia
// decidere al gioco, la seconda decide tutto da sé. In fondo all'elenco il
// personalizzato si leggeva invece come l'ultima delle palette, la più esotica.
//
// Poi le VARIANTI, e poi le palette. Per un periodo le palette sono state sei per asse,
// tagliate dalle dodici/quattordici di prima con l'argomento che un selettore libero rende
// inutile una lista lunga. L'argomento vale per le tinte VICINE — e infatti restano fuori
// 'cream-black', 'sand' e 'slate', che erano doppioni del classico, del legno e del
// blu/grigio — ma non per le famiglie: sei voci di cui tre chiaro-contro-scuro neutro e due
// col rosso non sono una scelta, sono la stessa idea cinque volte, ed è così che l'elenco
// si legge tutto uguale. Tornano quindi le quattro palette lontane (corallo, verde/viola,
// rosa/blu notte fra le pedine; mogano, oceano, viola/lavanda, rosa fra le scacchiere).
//
// Le quattro voci di variante non sono un gusto in più: i colori tradizionali dell'Italiana
// o dell'Inglese, prima, si potevano avere SOLO giocando a quella variante, perché li dava
// il tema 'auto' e nient'altro. Stanno in cima e adiacenti perché il gruppo si legga per
// posizione: l'elenco è piatto, non ha intestazioni.
//
// Dentro il gruppo l'ordine è quello della MasterMatrix (`variants.jsonc`, che lo dichiara
// come l'ordine mostrato in UI): Internazionale, Italiana, Inglese, Brasiliana. È lo stesso
// in entrambi i cataloghi di colore, ed è il punto: le due righe del pannello stanno una
// sotto l'altra, e due elenchi affiancati che partono da una variante diversa si leggono
// come due elenchi diversi. Lo pinna un test in `preferences.test.ts`.
export const PIECE_COLOR_THEMES: readonly PieceColorTheme[] = [
  'auto',
  'custom',
  'international',
  'italian',
  'english',
  'brazilian',
  'classic',
  'white-black',
  'red-black',
  'blue-red',
  'orange-blue',
  'silver-gold',
  'coral-teal',
  'green-purple',
  'pink-navy',
];
// Fra le scacchiere l'Internazionale non ha una voce propria: È 'wood-classic', che apre
// il gruppo delle varianti e ne porta l'etichetta.
export const BOARD_THEMES: readonly BoardTheme[] = [
  'auto',
  'custom',
  'wood-classic',
  'italian',
  'english',
  'brazilian',
  'walnut',
  'green-cream',
  'blue-grey',
  'marble',
  'high-contrast',
  'mahogany',
  'ocean',
  'purple-lavender',
  'rose',
];

/**
 * Le palette ritirate, per ID soltanto.
 *
 * Non sono più OFFERTE — non compaiono in nessun elenco e nessun client nuovo le spedisce
 * — ma restano ACCETTATE. La distinzione è ciò che evita un guasto preciso: lo schema
 * `.strict()` del server costruisce i suoi enum da `PIECE_COLOR_THEMES`/`BOARD_THEMES`, e
 * una scheda ancora aperta col bundle precedente continua a spedire il proprio tema. Se
 * fosse fuori dominio prenderebbe 400, e siccome il client ingoia l'errore del salvataggio
 * preferenze, da quel momento smetterebbero di salvarsi ANCHE audio, premove e cadenza,
 * in silenzio, fino al ricaricamento della pagina.
 *
 * Lato client i valori vengono convertiti in 'custom' con gli stessi colori: vedi
 * `retired-themes.ts` e `clampPreferences` nello store delle preferenze.
 *
 * Da qui si può anche TORNARE, ed è appena successo: sette id (corallo, verde/viola,
 * rosa/blu notte, mogano, oceano, viola/lavanda, rosa) sono rientrati negli elenchi
 * offerti. Il passaggio è sicuro nella direzione ritirato → offerto perché l'insieme
 * ACCETTATO è l'unione dei due, quindi non si restringe mai: cambia solo chi lo dichiara.
 * Va però tolta la voce corrispondente in `retired-themes.ts`, o `convertRetiredThemes`
 * continuerebbe a trasformare in 'custom' un tema che ora si può scegliere per nome.
 */
export const RETIRED_PIECE_COLOR_THEMES: readonly string[] = ['cream-black'];
export const RETIRED_BOARD_THEMES: readonly string[] = ['sand', 'slate'];

/**
 * Gli id d'icona dama RITIRATI, per la stessa ragione dei temi qui sopra: 'gem' è stato
 * rinominato 'diamond' e una scheda ancora aperta col bundle precedente continua a
 * spedirlo in OGNI patch delle preferenze. Fuori dominio prenderebbe 400 sull'intera
 * PATCH, e siccome il client ingoia l'errore del salvataggio smetterebbero di salvarsi
 * in silenzio anche audio, premove e cadenza.
 *
 * Lato client il valore viene convertito nel nuovo id: vedi `clampPreferences` nello
 * store delle preferenze. Qui non c'è nessuna palette da ricostruire, solo un nome.
 */
export const RETIRED_KING_ICONS: readonly string[] = ['gem'];

/**
 * La corona 'jewelled' — pietre sulla fascia e losanghe sulle punte — è stata sostituita dal
 * diadema. Vale la stessa regola degli emblemi ritirati: il server deve continuare ad
 * ACCETTARE il vecchio id, perché il client lo rispedisce in ogni patch finché non converte, e
 * un 400 sull'intera PATCH farebbe smettere di salvarsi anche audio, premove e cadenza.
 *
 * La conversione al nuovo id la fa `clampPreferences` nello store delle preferenze.
 */
export const RETIRED_CROWN_TYPES: readonly string[] = ['jewelled'];

/**
 * Le chiavi d'aspetto, cioè quelle che il pannello "Grafica" governa. Elencate qui
 * perché il reset delle preferenze (migrazione localStorage) deve azzerare ESATTAMENTE
 * queste e lasciare intatte suono, premove, cadenza e variante preferita.
 */
export const APPEARANCE_KEYS = [
  'pieceShape',
  'pieceStyle',
  'kingIcon',
  'crownType',
  'pieceColorTheme',
  'customPieceLight',
  'customPieceDark',
  'pieceMaterial',
  'boardTheme',
  'customBoardLight',
  'customBoardDark',
  'boardMaterial',
  'boardNotation',
] as const satisfies readonly (keyof UserPreferences)[];

/** Le quattro chiavi di colore personalizzato, con il tema che le rende attive. */
export const CUSTOM_COLOR_KEYS = [
  'customPieceLight',
  'customPieceDark',
  'customBoardLight',
  'customBoardDark',
] as const satisfies readonly (keyof UserPreferences)[];

/**
 * Esadecimale a sei cifre con il cancelletto. È il formato che sia i gradienti SVG
 * sia le manipolazioni di tinta (`lighten`/`darken`) danno per scontato: un valore
 * in altra notazione non fallirebbe, produrrebbe silenziosamente colori sbagliati.
 */
export const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

export function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && HEX_COLOR_RE.test(value);
}

/**
 * Lo stile è privo di spessore. Su questi stili la dama non può avere volume, quindi
 * l'icona è l'unico segnale che la distingue dalla pedina e `kingIcon: 'none'` va vietata.
 *
 * Vive qui e non nel catalogo di rendering del web perché la regola è una proprietà del
 * DOMINIO — la applicano il pannello impostazioni, lo store e il renderer.
 */
export function isFlatStyle(style: PieceStyle): boolean {
  return style === 'flat-plain' || style === 'flat-engraved';
}

/** Preset offerti per il target dell'obiettivo giornaliero (partite/giorno). */
export const DAILY_GOAL_PRESETS: readonly number[] = [1, 3, 5, 10];

/** Valid values for `preferredVariant` — lowercase strings of every active variant. */
export const PREFERRED_VARIANTS: readonly string[] = ACTIVE_VARIANTS.map((v) =>
  v.toLowerCase(),
);

/** Valid values for `preferredCadence` — the four rated time-control categories. */
export const PREFERRED_CADENCES: readonly PreferredCadence[] = [
  'bullet',
  'blitz',
  'rapid',
  'classical',
];

/**
 * I valori ammessi per `uiTheme`.
 *
 * NON va aggiunto ad `APPEARANCE_KEYS`: quell'elenco e' cio' che il passo
 * `version < 1` di `migratePersisted` AZZERA, quindi un futuro reset
 * dell'aspetto rispedirebbe al chiaro chi aveva scelto il buio.
 */
export const UI_THEMES: readonly UiTheme[] = ['light', 'dark'];
